import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @test-file: Dropdown.test.tsx
 * @test-coverage: 13 tests (toggle, click-outside, escape, keyboard nav, disabled states, variants)
 * @last-updated: 2025-12-28
 */
import React, { useState, useRef, useEffect } from 'react';
const DropdownContext = React.createContext({
    isOpen: false,
    toggle: () => { },
    close: () => { },
});
export const Dropdown = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toggle = () => setIsOpen((prev) => !prev);
    const close = () => setIsOpen(false);
    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                close();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);
    // Escape key to close
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen]);
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        .ui-dropdown {
          position: relative;
          display: inline-block;
        }

        .ui-dropdown-toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 6px;
          border: 1px solid var(--border, #ddd);
          background: var(--card, #fff);
          color: var(--text, #333);
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .ui-dropdown-toggle:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .ui-dropdown-toggle:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ui-dropdown-toggle.variant-primary {
          background: linear-gradient(135deg, var(--brand, #ff7b2f), #ffd166);
          color: #0b0f14;
          border: none;
        }

        .ui-dropdown-toggle.variant-secondary {
          background: #6c757d;
          color: #fff;
          border: none;
        }

        .ui-dropdown-toggle.variant-success {
          background: #28a745;
          color: #fff;
          border: none;
        }

        .ui-dropdown-toggle.variant-danger {
          background: #dc3545;
          color: #fff;
          border: none;
        }

        .ui-dropdown-toggle.variant-warning {
          background: #ffc107;
          color: #000;
          border: none;
        }

        .ui-dropdown-toggle.variant-info {
          background: #17a2b8;
          color: #fff;
          border: none;
        }

        .ui-dropdown-toggle.variant-outline-primary {
          background: transparent;
          border-color: var(--brand, #ff7b2f);
          color: var(--brand, #ff7b2f);
        }

        .ui-dropdown-toggle.variant-outline-primary:hover:not(:disabled) {
          background: rgba(255, 123, 47, 0.1);
        }

        .ui-dropdown-toggle.variant-outline-secondary {
          background: transparent;
          border-color: #6c757d;
          color: #6c757d;
        }

        .ui-dropdown-toggle.variant-outline-secondary:hover:not(:disabled) {
          background: rgba(108, 117, 125, 0.1);
        }

        .ui-dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          margin-left: 4px;
          transition: transform 0.2s ease;
        }

        .ui-dropdown-toggle.open::after {
          transform: rotate(180deg);
        }

        .ui-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          min-width: 200px;
          background: var(--card, #fff);
          border: 1px solid var(--border, #ddd);
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          padding: 4px 0;
          max-height: 400px;
          overflow-y: auto;
        }

        .ui-dropdown-item {
          display: block;
          width: 100%;
          padding: 8px 16px;
          border: none;
          background: transparent;
          color: var(--text, #333);
          font-size: 14px;
          text-align: left;
          cursor: pointer;
          transition: background 0.1s ease;
        }

        .ui-dropdown-item:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
        }

        .ui-dropdown-item:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ui-dropdown-divider {
          height: 1px;
          margin: 4px 0;
          background: var(--border, #ddd);
          border: none;
        }

        .ui-dropdown-header {
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 700;
          color: var(--muted, #888);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      ` }), _jsx(DropdownContext.Provider, { value: { isOpen, toggle, close }, children: _jsx("div", { className: "ui-dropdown", ref: dropdownRef, children: children }) })] }));
};
const DropdownToggle = ({ variant, disabled, id, children }) => {
    const { isOpen, toggle } = React.useContext(DropdownContext);
    const variantClass = variant ? `variant-${variant}` : '';
    const openClass = isOpen ? 'open' : '';
    return (_jsx("button", { id: id, className: `ui-dropdown-toggle ${variantClass} ${openClass}`, onClick: toggle, disabled: disabled, type: "button", children: children }));
};
const DropdownMenu = ({ children }) => {
    const { isOpen } = React.useContext(DropdownContext);
    const menuRef = useRef(null);
    // Focus management for keyboard navigation
    useEffect(() => {
        if (isOpen && menuRef.current) {
            const firstItem = menuRef.current.querySelector('.ui-dropdown-item:not(:disabled)');
            firstItem?.focus();
        }
    }, [isOpen]);
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "ui-dropdown-menu", ref: menuRef, children: children }));
};
const DropdownItem = ({ onClick, disabled, children }) => {
    const { close } = React.useContext(DropdownContext);
    const handleClick = () => {
        if (!disabled && onClick) {
            onClick();
            close();
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
        }
        else if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextItem = event.target.nextElementSibling;
            if (nextItem?.classList.contains('ui-dropdown-item')) {
                nextItem.focus();
            }
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevItem = event.target.previousElementSibling;
            if (prevItem?.classList.contains('ui-dropdown-item')) {
                prevItem.focus();
            }
        }
    };
    return (_jsx("button", { className: "ui-dropdown-item", onClick: handleClick, onKeyDown: handleKeyDown, disabled: disabled, type: "button", tabIndex: 0, children: children }));
};
const DropdownDivider = () => {
    return _jsx("hr", { className: "ui-dropdown-divider" });
};
const DropdownHeader = ({ children }) => {
    return _jsx("div", { className: "ui-dropdown-header", children: children });
};
Dropdown.Toggle = DropdownToggle;
Dropdown.Menu = DropdownMenu;
Dropdown.Item = DropdownItem;
Dropdown.Divider = DropdownDivider;
Dropdown.Header = DropdownHeader;
